/**
 * Shared source-message accordion used in both Digest History and Action Items citation sidebar.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoCheckmark, IoCopyOutline, IoSearchOutline } from "react-icons/io5";
import { timestampFormatter } from "../../../core/utils/timestampFormatter";
import { InlineSpinner } from "../../../core/components/InlineSpinner";

// Re-exported for existing importers of this module -- the implementation
// now lives in core/components/InlineSpinner.js (Phase 5a), since it's a
// generic loading indicator with no action-item-specific logic.
export { InlineSpinner };

const TIMESTAMP_MINUTE_FORMAT = { includeSeconds: false };

/** Leading/trailing punctuation only (Unicode P category), e.g. quotes and ellipses from snippets. */
const LEADING_TRAILING_PUNCT_RE = /^[\p{P}]+|[\p{P}]+$/gu;

/**
 * Removes punctuation from the start and end of a string (repeatedly until stable).
 *
 * @example
 * stripLeadingTrailingPunctuation('"In addition, …"') → "In addition, …" (no outer quotes)
 * stripLeadingTrailingPunctuation('  «hello»  ') → "hello"
 */
const stripLeadingTrailingPunctuation = (value) => {
  let s = String(value || "").trim();
  let prev;
  do {
    prev = s;
    s = s.replace(LEADING_TRAILING_PUNCT_RE, "").trim();
  } while (s !== prev);
  return s;
};

/**
 * Formats stored Gmail JSON (or plain text) for display in the source panel.
 *
 * For forwarded/reply emails, extraction stores the top line in `current_message`
 * and quoted/forwarded content in `last_message`. Both are included so the panel
 * matches what the LLM sees during primitive extraction.
 *
 * @example
 * Input: '{"current_message":"Get Outlook for iOS","last_message":"From: Zoom\\nMeeting summary..."}'
 * Output: "Get Outlook for iOS\n\nFrom: Zoom\nMeeting summary..."
 */
export const formatSourceMessageContent = (rawContent) => {
  const rawText = String(rawContent || "").trim();
  if (!rawText) return "No source content available.";
  const normalizeText = (value) =>
    String(value || "")
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "  ")
      .trim();
  const combineEmailBody = (parsed) => {
    const current = parsed.current_message || parsed.currentMessage || "";
    const last = parsed.last_message || parsed.lastMessage || "";
    const parts = [];
    if (typeof current === "string" && current.trim()) parts.push(current.trim());
    if (typeof last === "string" && last.trim()) parts.push(last.trim());
    return parts.join("\n\n");
  };
  try {
    const parsed = JSON.parse(rawText);
    if (parsed && typeof parsed === "object") {
      const combined = combineEmailBody(parsed);
      if (combined) return normalizeText(combined);
      const fallback = parsed.body || parsed.text || parsed.snippet;
      if (typeof fallback === "string" && fallback.trim()) return normalizeText(fallback);
    }
  } catch (_err) {
    return normalizeText(rawText);
  }
  return normalizeText(rawText);
};

export const cleanupSourceMarkdown = (rawMarkdown) => {
  const isSeparatorLine = (line) =>
    /^\s*\|?\s*(?:---\s*)+(?:\|\s*(?:---\s*)+)*\|?\s*$/.test(line);
  const isEmptyPipeLine = (line) => /^\s*\|\s*(?:\|\s*)+$/.test(line);
  const unwrapPipePrefixedText = (line) => {
    const match = line.match(/^\s*\|\s*\|\s*(.+)$/);
    return match ? match[1].trim() : line;
  };
  const cleanedLines = String(rawMarkdown || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map(unwrapPipePrefixedText)
    .filter((line) => !isSeparatorLine(line))
    .filter((line) => !isEmptyPipeLine(line))
    .map((line) => line.replace(/\s+$/, ""));
  const compacted = [];
  cleanedLines.forEach((line) => {
    const previous = compacted[compacted.length - 1];
    if (!line.trim() && !previous?.trim()) return;
    compacted.push(line);
  });
  return compacted.join("\n").trim();
};

export const parseSourceContentBlocks = (content) => {
  const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];
  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    blocks.push({ type: "paragraph", text: paragraphLines.join(" ").trim() });
    paragraphLines = [];
  };
  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: "list", items: [...listItems] });
    listItems = [];
  };
  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) { flushParagraph(); flushList(); return; }
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) { flushParagraph(); flushList(); blocks.push({ type: "heading", level: headingMatch[1].length, text: headingMatch[2].trim() }); return; }
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) { flushParagraph(); listItems.push(bulletMatch[1].trim()); return; }
    const numberedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (numberedMatch) { flushParagraph(); listItems.push(numberedMatch[1].trim()); return; }
    flushList();
    paragraphLines.push(line);
  });
  flushParagraph();
  flushList();
  return blocks;
};

const extractDisplayName = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  const angledMatch = rawValue.match(/^(.+?)\s*<[^>]+>$/);
  if (angledMatch) return angledMatch[1].trim();
  if (rawValue.includes("@")) return "";
  return rawValue;
};

/**
 * Uppercases only the first visible character in a title string.
 *
 * @example
 * ensureLeadingCapital("meeting tasks") -> "Meeting tasks"
 * ensureLeadingCapital("Meeting tasks") -> "Meeting tasks"
 */
const ensureLeadingCapital = (value) => {
  const text = String(value || "");
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatSourceParticipants = (participants) => {
  const fmt = (p) => {
    if (typeof p === "string") return extractDisplayName(p);
    if (!p || typeof p !== "object") return "";
    return extractDisplayName(p.name || p.display_name || p.full_name || p.value || "");
  };
  if (Array.isArray(participants)) {
    const cleaned = participants.map(fmt).filter(Boolean);
    return cleaned.length ? cleaned.join(", ") : "";
  }
  return fmt(participants);
};

/**
 * Renders one source-message accordion card.
 * Identical markup to the cards in Digest History right panel.
 *
 * Props:
 *   sourceMessage  – message row object
 *   isExpanded     – boolean
 *   cacheEntry     – { loading, error, content } | undefined
 *   onToggle       – (sourceMessage, triggerElement) => void
 *   timeLabel      – optional pre-formatted time (e.g. action-item meeting time)
 *   variant        – "transcript": no card header (the task panel already names the
 *                    meeting), body always open, and the find bar stays closed behind a
 *                    magnifier button (a dot on it means a search is active, e.g. the
 *                    source has a snippet to jump to)
 */
const SourceMessageAccordion = ({
  sourceMessage,
  isExpanded: isExpandedProp,
  cacheEntry,
  onToggle,
  timeLabel = "",
  variant = "default",
}) => {
  const isTranscript = variant === "transcript";
  const isExpanded = isTranscript || isExpandedProp;
  const contentRef = useRef(null);
  const matchesRef = useRef([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const messageId = String(sourceMessage.message_id || "");
  // Manually pasted/captured sources render verbatim (exact whitespace and line
  // breaks, no markdown reflow) -- see handleBulkAddGlobalActionItems / loadSourceMessages,
  // which set this for captures. Connector content (email, digest text) still goes
  // through the markdown cleanup + paragraph reflow below, which those legitimately need.
  const isVerbatim = Boolean(sourceMessage.verbatim);
  const participantsLabel = formatSourceParticipants(sourceMessage.participants);
  const senderLabel = extractDisplayName(sourceMessage.sender);
  const titleSecondaryLabel = senderLabel || participantsLabel;
  const metaSecondaryLabel = titleSecondaryLabel === senderLabel ? participantsLabel : senderLabel;
  const displayTitle = ensureLeadingCapital(sourceMessage.subject || "Untitled source record");
  const resolvedTimeLabel = String(timeLabel || "").trim()
    || (sourceMessage.sent_time
      ? timestampFormatter(sourceMessage.sent_time, TIMESTAMP_MINUTE_FORMAT)
      : "-");
  useEffect(() => {
    if (!isExpanded) return;
    setSearchQuery(stripLeadingTrailingPunctuation(sourceMessage.source_snippet || ""));
  }, [isExpanded, sourceMessage.source_snippet]);

  const clearSnippetHighlights = (contentEl) => {
    const highlighted = contentEl.querySelectorAll("mark[data-source-snippet-highlight='true']");
    highlighted.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(mark.textContent || ""), mark);
      parent.normalize();
    });
  };

  const findMatches = (contentEl, query) => {
    const normalizedQuery = stripLeadingTrailingPunctuation(String(query || "").replace(/\s+/g, " "));
    if (!normalizedQuery) return [];
    const loweredQuery = normalizedQuery.toLowerCase();

    const matches = [];
    const walker = document.createTreeWalker(contentEl, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();
    while (currentNode) {
      const nodeText = String(currentNode.nodeValue || "");
      const loweredNodeText = nodeText.toLowerCase();
      let startIndex = loweredNodeText.indexOf(loweredQuery);
      while (startIndex >= 0) {
        matches.push({
          node: currentNode,
          startIndex,
          endIndex: startIndex + normalizedQuery.length,
        });
        startIndex = loweredNodeText.indexOf(loweredQuery, startIndex + loweredQuery.length);
      }
      currentNode = walker.nextNode();
    }
    return matches;
  };

  const highlightMatchByIndex = useCallback((nextIndex) => {
    const contentEl = contentRef.current;
    if (!contentEl) return;
    clearSnippetHighlights(contentEl);
    const matches = matchesRef.current || [];
    if (!matches.length || nextIndex < 0 || nextIndex >= matches.length) return;

    const match = matches[nextIndex];
    if (!match?.node?.isConnected) return;
    try {
      const range = document.createRange();
      range.setStart(match.node, match.startIndex);
      range.setEnd(match.node, match.endIndex);
      const mark = document.createElement("mark");
      mark.dataset.sourceSnippetHighlight = "true";
      mark.className = "business-source-content__snippet-highlight";
      range.surroundContents(mark);
      mark.scrollIntoView({ behavior: "smooth", block: "center" });
      setActiveMatchIndex(nextIndex);
    } catch (_err) {
      // Ignore range failures and keep content unchanged.
    }
  }, []);

  const handleMoveMatch = (step) => {
    const matches = matchesRef.current || [];
    if (!matches.length) return;
    const baseIndex = activeMatchIndex >= 0 ? activeMatchIndex : 0;
    const nextIndex = (baseIndex + step + matches.length) % matches.length;
    highlightMatchByIndex(nextIndex);
  };

  /**
   * Returns the user-visible source body (not the raw stored JSON payload).
   *
   * @example
   * Input: cacheEntry.content = '{"current_message":"Hi","last_message":"Summary"}'
   * Output: "Hi\n\nSummary"
   */
  const getDisplaySourceText = useCallback(() => {
    const contentEl = contentRef.current;
    if (contentEl) {
      const visibleText = String(contentEl.innerText || "").trim();
      if (visibleText) return visibleText;
    }
    const rawContent = String(cacheEntry?.content || "").trim();
    if (!rawContent) return "";
    const formatted = formatSourceMessageContent(rawContent);
    if (isVerbatim) return formatted;
    return cleanupSourceMarkdown(formatted);
  }, [cacheEntry?.content, isVerbatim]);

  /**
   * Copies the displayed source body to clipboard.
   *
   * @example
   * Input: panel shows "Next Steps\nTest action item extraction..."
   * Output: Clipboard now contains that same visible text
   */
  const handleCopySource = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const displayText = getDisplaySourceText().trim();
    if (!displayText) return;
    try {
      await navigator.clipboard.writeText(displayText);
      setCopySuccess(true);
      window.setTimeout(() => setCopySuccess(false), 1800);
    } catch (_err) {
      // Ignore clipboard failures silently; user can still select manually.
    }
  };

  useEffect(() => {
    if (!isExpanded || cacheEntry?.loading || cacheEntry?.error) return;
    const contentEl = contentRef.current;
    if (!contentEl) return;
    clearSnippetHighlights(contentEl);

    const buildSnippetCandidates = (snippet) => {
      const normalized = stripLeadingTrailingPunctuation(String(snippet || "").replace(/\s+/g, " "));
      if (!normalized) return [];
      const words = normalized.split(" ").filter(Boolean);
      const windows = [];
      for (let i = 0; i < words.length - 5; i += 4) {
        windows.push(words.slice(i, i + 6).join(" "));
      }
      const progressiveTruncation = [];
      for (let remaining = words.length - 1; remaining >= 1; remaining -= 1) {
        progressiveTruncation.push(words.slice(0, remaining).join(" "));
      }
      return [
        normalized,
        normalized.slice(0, 180),
        normalized.slice(0, 120),
        normalized.slice(0, 80),
        ...windows,
        ...progressiveTruncation,
      ]
        .map((value) => value.trim())
        .filter(Boolean);
    };

    const snippetCandidates = buildSnippetCandidates(searchQuery);
    if (!snippetCandidates.length) {
      matchesRef.current = [];
      setMatchCount(0);
      setActiveMatchIndex(-1);
      return;
    }

    let resolvedMatches = [];
    for (const candidate of snippetCandidates) {
      resolvedMatches = findMatches(contentEl, candidate);
      if (resolvedMatches.length) break;
    }

    matchesRef.current = resolvedMatches;
    setMatchCount(resolvedMatches.length);
    if (!resolvedMatches.length) {
      setActiveMatchIndex(-1);
      return;
    }
    highlightMatchByIndex(0);
  }, [
    isExpanded,
    cacheEntry?.loading,
    cacheEntry?.error,
    cacheEntry?.content,
    searchQuery,
    highlightMatchByIndex,
  ]);

  const copyButton = (
    <button
      type="button"
      className={`business-source-accordion__copy ${copySuccess ? "is-copied" : ""}`}
      onClick={handleCopySource}
      aria-label="Copy original source content"
      title={copySuccess ? "Copied" : "Copy original source"}
    >
      {copySuccess ? <IoCheckmark aria-hidden="true" /> : <IoCopyOutline aria-hidden="true" />}
    </button>
  );

  return (
    <article
      key={messageId}
      className={`business-source-accordion${isTranscript ? " business-source-accordion--transcript" : ""}`}
    >
      {isTranscript ? null : (
      <button
        type="button"
        className="business-source-accordion__header"
        onClick={(event) => onToggle(sourceMessage, event.currentTarget)}
      >
        <div className="business-source-accordion__title">
          <div className="business-source-accordion__title-row">
            <span className="business-source-accordion__chevron" aria-hidden="true" />
            <strong>{displayTitle}</strong>
          </div>
          {titleSecondaryLabel ? <span>{titleSecondaryLabel}</span> : null}
        </div>
        <div className="business-source-accordion__meta">
          {copyButton}
          <span>{resolvedTimeLabel}</span>
          {metaSecondaryLabel ? (
            <span className="business-source-accordion__meta-secondary">{metaSecondaryLabel}</span>
          ) : null}
        </div>
      </button>
      )}
      {isExpanded ? (
        <div className="business-source-accordion__body">
          {cacheEntry?.loading ? <InlineSpinner text="Loading full email content..." /> : null}
          {cacheEntry?.error ? <p className="error-message">{cacheEntry.error}</p> : null}
          {!cacheEntry?.loading && !cacheEntry?.error ? (
            <>
              {(() => {
                const searchControls = (
                  <>
                    <input
                      type="text"
                      className="business-source-search__input"
                      value={searchQuery}
                      autoFocus={isTranscript}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (isTranscript && event.key === "Escape") setSearchOpen(false);
                      }}
                      placeholder={isTranscript ? "Search transcript" : "Search inside source content..."}
                      aria-label="Search text in source content"
                    />
                    <span className="business-source-search__count">
                      {matchCount > 0 ? `${activeMatchIndex + 1}/${matchCount}` : "0/0"}
                    </span>
                    <button
                      type="button"
                      className="business-source-search__nav"
                      onClick={() => handleMoveMatch(-1)}
                      disabled={matchCount <= 1}
                      aria-label="Previous match"
                      title="Previous match"
                    >
                      <span aria-hidden="true">↑</span>
                    </button>
                    <button
                      type="button"
                      className="business-source-search__nav"
                      onClick={() => handleMoveMatch(1)}
                      disabled={matchCount <= 1}
                      aria-label="Next match"
                      title="Next match"
                    >
                      <span aria-hidden="true">↓</span>
                    </button>
                  </>
                );
                if (!isTranscript) {
                  return (
                    <div className="business-source-search" role="search">
                      <span className="business-source-search__label">Find</span>
                      {searchControls}
                    </div>
                  );
                }
                // Transcript: one row. Find box opens in line with the magnifier, which keeps its place.
                return (
                  <div className="business-source-toolbar">
                    {searchOpen ? (
                      <div className="business-source-toolbar__search" role="search">
                        {searchControls}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className={`business-source-toolbar__btn${searchOpen ? " is-active" : ""}`}
                      onClick={() => setSearchOpen((open) => !open)}
                      aria-label="Search transcript"
                      aria-expanded={searchOpen}
                      title="Search transcript"
                    >
                      <IoSearchOutline aria-hidden="true" />
                      {searchQuery.trim() && !searchOpen ? (
                        <span className="business-source-toolbar__dot" data-testid="source-search-dot" />
                      ) : null}
                    </button>
                    {copyButton}
                  </div>
                );
              })()}
              <div className="business-source-content" ref={contentRef}>
                {isVerbatim ? (
                  <pre className="business-source-content__verbatim">
                    {formatSourceMessageContent(cacheEntry?.content)}
                  </pre>
                ) : (
                  parseSourceContentBlocks(
                    cleanupSourceMarkdown(formatSourceMessageContent(cacheEntry?.content))
                  ).map((block, index) => {
                    const key = `source-content-${messageId}-${index}`;
                    if (block.type === "heading") {
                      const HeadingTag = `h${Math.min(block.level, 4)}`;
                      return <HeadingTag key={key}>{block.text}</HeadingTag>;
                    }
                    if (block.type === "list") {
                      return (
                        <ul key={key}>
                          {block.items.map((item, itemIndex) => (
                            <li key={`${key}-item-${itemIndex}`}>{item}</li>
                          ))}
                        </ul>
                      );
                    }
                    return <p key={key}>{block.text}</p>;
                  })
                )}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export default SourceMessageAccordion;
